import { formatDate as format, formatTime } from '../lib/format';
import StatCard from '../components/ui/StatCard';
import PageContent from '../components/ui/PageContent';
import PageHeader from '../components/ui/PageHeader';
import { TableEmpty, Table, TableHead, TableRow, TableHeaderCell, TableCell } from '../components/ui/Table';
import FieldLabel from '../components/ui/FieldLabel';
import UiSelect from '../components/ui/Select';
import Button from '../components/ui/Button';
import Tabs from '../components/ui/Tabs';
import { getAllocationDisplayAddress } from '../utils/workerAllocationAddress';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Section from '../components/Section';
import LoadingPopup from '../components/LoadingPopup';
import { getWorkerWeeklyInvoice } from '../utils/workerWeeklyInvoice';
import { ReceiptIcon, CalendarIcon, ChevronRightIcon, ClipboardListIcon, ClockIcon, VerifiedIcon, XIcon } from '../components/NavIcons';
import { postWithAuth } from '../common';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { toBusinessWallDate } from '../utils/businessTime';
const renderSiteAddress = value => {
    const address = String(value || '').trim()
    if (!address) return '-'
    const commaIndex = address.indexOf(',')
    if (commaIndex < 0) return address
    const street = address.slice(0, commaIndex).trim()
    const location = address.slice(commaIndex + 1).trim()
    return <><span>{street},</span><br /><span>{location}</span></>
}


const calendarMonthOptions = Array.from({ length: 12 }, (_, month) => ({
    value: month,
    label: format(new Date(2026, month, 1), 'MMMM'),
}))

const getCalendarYearOptions = activeYear => Array.from({ length: 11 }, (_, index) => activeYear - 5 + index)

const getMonday = value => {
    const date = new Date(value)
    const day = date.getDay() || 7
    date.setDate(date.getDate() - day + 1)
    date.setHours(0, 0, 0, 0)
    return date
}

const isHistoricalIncompleteTimesheet = (row, todayStart) => {
    const status = String(row?.status || '').toLowerCase()
    if (row?.clockIn && row?.clockOut) return false
    if (row?.clockIn && !row?.clockOut) return true
    if (status === 'active') return true
    if (status === 'completed') return false
    return status === 'accepted' && (toBusinessWallDate(row?.date) || new Date(0)) < todayStart
}

const renderDateTimeCell = value => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return <><span>{format(date, 'EEE, d MMM yyyy')}</span><br /><span>{formatTime(date)}</span></>
}

const WorkerOverview = () => {
    const navigate = useNavigate()
    const activeOrg = useSelector(state => state.user.activeOrg)
    const [timeSheets, setTimeSheets] = useState([])
    const [workerLeaves, setWorkerLeaves] = useState([])
    const [loading, setLoading] = useState(false)
    const [calendarDate, setCalendarDate] = useState(new Date())
    const [calendarViewMode, setCalendarViewMode] = useState('month')
    const [calendarQuickView, setCalendarQuickView] = useState(null)
    const [calendarActiveStartDate, setCalendarActiveStartDate] = useState(() => {
        const date = new Date()
        return new Date(date.getFullYear(), date.getMonth(), 1)
    })

    useEffect(() => {
        if (!activeOrg?.id) return
        const load = async () => {
            try {
                setLoading(true)
                const [timeSheetResponse, leaveResponse] = await Promise.all([
                    postWithAuth('fetchTimeSheet', { orgId: activeOrg.id }),
                    postWithAuth('fetchWorkerLeaves', { orgId: activeOrg.id }),
                ])
                if (timeSheetResponse?.err) throw new Error(timeSheetResponse.err)
                if (leaveResponse?.err) throw new Error(leaveResponse.err)
                setTimeSheets(timeSheetResponse?.data || [])
                setWorkerLeaves(leaveResponse?.data || [])
            } catch (error) {
                toast.error(error.message)
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [activeOrg?.id])

    const pendingAllocations = useMemo(() => timeSheets
        .filter(timeSheet => !timeSheet.status)
        .sort((first, second) => new Date(first.date) - new Date(second.date)), [timeSheets])

    const upcomingAllocations = useMemo(() => {
        const today = toBusinessWallDate(new Date())
        today.setHours(0, 0, 0, 0)
        return timeSheets
            .filter(timeSheet => timeSheet.status === 'Accepted' && (toBusinessWallDate(timeSheet.date) || new Date(0)) >= today)
            .sort((first, second) => new Date(first.date) - new Date(second.date))
    }, [timeSheets])

    const pendingRowsForDisplay = pendingAllocations
    const upcomingRowsForDisplay = upcomingAllocations
    const incompleteTimesheets = useMemo(() => {
        const today = toBusinessWallDate(new Date())
        today.setHours(0, 0, 0, 0)
        return timeSheets.filter(row => isHistoricalIncompleteTimesheet(row, today))
            .sort((first, second) => new Date(second.date || 0) - new Date(first.date || 0))
    }, [timeSheets])
    const completedCalendarAllocations = timeSheets.filter(row => String(row.status || '').toLowerCase() === 'completed')
    const calendarPendingDateKeys = new Set(
        pendingRowsForDisplay
            .filter(row => row.date && toBusinessWallDate(row.date) >= toBusinessWallDate(new Date()))
            .map(row => format(new Date(row.date), 'yyyy-MM-dd'))
    )
    const calendarGreenAllocationDateKeys = new Set(
        [...upcomingRowsForDisplay, ...completedCalendarAllocations]
            .filter(row => row.date)
            .map(row => format(new Date(row.date), 'yyyy-MM-dd'))
    )
    const calendarAllocations = [
        ...pendingRowsForDisplay,
        ...upcomingRowsForDisplay,
        ...completedCalendarAllocations,
    ]
    const getCalendarAllocations = date => {
        const dateKey = format(date, 'yyyy-MM-dd')
        return calendarAllocations.filter(row => row.date && format(new Date(row.date), 'yyyy-MM-dd') === dateKey)
    }
    const showCalendarQuickView = (event, date) => {
        const allocations = getCalendarAllocations(date)
        if (!allocations.length) return
        const bounds = event.currentTarget.getBoundingClientRect()
        setCalendarQuickView({
            allocation: allocations[0],
            left: Math.max(100, Math.min(window.innerWidth - 100, bounds.left + bounds.width / 2)),
            top: bounds.top - 8,
        })
    }
    const renderCalendarHoverTarget = date => {
        if (!getCalendarAllocations(date).length) return null
        return <span
            className='workerOverviewCalendarHoverTarget'
            onMouseEnter={event => showCalendarQuickView(event, date)}
            onMouseLeave={() => setCalendarQuickView(null)}
        />
    }
    const getCalendarMarkerClass = date => {
        const dateKey = format(date, 'yyyy-MM-dd')
        if (calendarPendingDateKeys.has(dateKey)) return 'workerOverviewCalendarHasAllocation workerOverviewCalendarPendingAllocation'
        if (calendarGreenAllocationDateKeys.has(dateKey)) return 'workerOverviewCalendarHasAllocation'
        return ''
    }
    const calendarWeekStart = getMonday(calendarDate)
    const calendarWeekDays = Array.from({ length: 7 }, (_, index) => {
        const date = new Date(calendarWeekStart)
        date.setDate(date.getDate() + index)
        return date
    })

    const shiftsThisWeek = useMemo(() => {
        const now = new Date()
        const monday = new Date(now)
        const day = monday.getDay() || 7
        monday.setDate(monday.getDate() - day + 1)
        monday.setHours(0, 0, 0, 0)
        const nextMonday = new Date(monday)
        nextMonday.setDate(nextMonday.getDate() + 7)
        return timeSheets.filter(timeSheet => {
            const date = new Date(timeSheet.date)
            return timeSheet.status && date >= monday && date < nextMonday
        }).length
    }, [timeSheets])

    const hoursWorkedThisWeek = useMemo(() => {
        const now = new Date()
        const monday = new Date(now)
        const day = monday.getDay() || 7
        monday.setDate(monday.getDate() - day + 1)
        monday.setHours(0, 0, 0, 0)
        const nextMonday = new Date(monday)
        nextMonday.setDate(nextMonday.getDate() + 7)
        const total = timeSheets.reduce((hours, timeSheet) => {
            if (!timeSheet.clockIn || !timeSheet.clockOut) return hours
            const shiftDate = new Date(timeSheet.date)
            if (shiftDate < monday || shiftDate >= nextMonday) return hours
            const elapsed = (new Date(timeSheet.clockOut) - new Date(timeSheet.clockIn)) / 3600000
            const unpaidBreak = timeSheet.breakIncludedInPay ? 0 : (Number(timeSheet.breakMinutes) || 0) / 60
            return hours + Math.max(0, elapsed - unpaidBreak)
        }, 0)
        return total.toFixed(2)
    }, [timeSheets])

    const weeklyInvoice = useMemo(() => getWorkerWeeklyInvoice(timeSheets), [timeSheets])

    const upcomingLeaves = useMemo(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return workerLeaves
            .filter(leave => new Date(leave.endDate) >= today)
            .sort((first, second) => new Date(first.startDate) - new Date(second.startDate))
    }, [workerLeaves])
    const leaveRowsForDisplay = upcomingLeaves

    const openAllocationAction = (action, allocation) => {
        navigate('/workers?view=allocations', {
            state: {
                workerAllocationAction: action,
                workerAllocationId: allocation.id,
            },
        })
    }
    const AllocationTable = ({ rows, pending = false }) => (
        <div className='workerAllocationTableWrap workerOverviewTableWrap'>
            <Table className='workerAllocationTable'>
                <TableHead><TableRow><TableHeaderCell>Date &amp; Time</TableHeaderCell><TableHeaderCell>Client</TableHeaderCell><TableHeaderCell>Site Name</TableHeaderCell><TableHeaderCell>Site Address</TableHeaderCell><TableHeaderCell>Action</TableHeaderCell></TableRow></TableHead>
                <tbody>
                    {rows.map(row => (
                        <TableRow key={row.id} className={pending ? 'workerOverviewPendingAllocationRow' : ''}>
                            <TableCell data-label='Date & Time' className='workerOverviewDateTimeCell'><span>{format(new Date(row.date), 'EEE, d MMM yyyy')}</span><br /><span>{formatTime(new Date(row.date))}</span></TableCell>
                            <TableCell data-label='Client'>{row.clientName || row.client || '-'}</TableCell>
                            <TableCell data-label='Site Name'>{row.siteName || row.site || '-'}</TableCell>
                            <TableCell data-label='Site Address'>{renderSiteAddress(getAllocationDisplayAddress(row))}</TableCell>
                            <TableCell data-label='Action' className='workerOverviewActionCell'>
                                {!pending && <Button variant="secondary" size="sm" className='workerAllocationAction workerAllocationActionView' onClick={event => { event.stopPropagation(); openAllocationAction('view', row) }}>View</Button>}
                                {pending && <>
                                    <Button variant="secondary" size="icon" title='Accept allocation' aria-label='Accept allocation' className='workerAllocationAction workerAllocationActionAccept workerOverviewIconAction' onClick={event => { event.stopPropagation(); openAllocationAction('accept', row) }}><VerifiedIcon /></Button>
                                    <Button variant="secondary" size="icon" title='Reject allocation' aria-label='Reject allocation' className='workerAllocationAction workerAllocationActionReject workerOverviewIconAction' onClick={event => { event.stopPropagation(); openAllocationAction('reject', row) }}><XIcon /></Button>
                                </>}
                            </TableCell>
                        </TableRow>
                    ))}
                    {!loading && rows.length === 0 && <TableEmpty  colSpan={5}>No allocations yet.</TableEmpty>}
                </tbody>
            </Table>
        </div>
    )
    const IncompleteTimesheetTable = () => (
        <div className='workerAllocationTableWrap workerOverviewTableWrap'>
            <Table className='workerAllocationTable'>
                <TableHead><TableRow><TableHeaderCell>Date &amp; Time</TableHeaderCell><TableHeaderCell>Client</TableHeaderCell><TableHeaderCell>Site</TableHeaderCell><TableHeaderCell>Shift Start</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell>Action</TableHeaderCell></TableRow></TableHead>
                <tbody>
                    {incompleteTimesheets.map(row => (
                        <TableRow key={row.id} className='workerPendingAllocationRow'>
                            <TableCell data-label='Date & Time'>{renderDateTimeCell(row.date)}</TableCell>
                            <TableCell data-label='Client'>{row.clientName || '-'}</TableCell>
                            <TableCell data-label='Site'>{renderSiteAddress(getAllocationDisplayAddress(row))}</TableCell>
                            <TableCell data-label='Shift Start'>{renderDateTimeCell(row.clockIn)}</TableCell>
                            <TableCell data-label='Status'>Incomplete</TableCell>
                            <TableCell data-label='Action'><Button variant="secondary" size="sm" className='workerAllocationAction workerAllocationActionView' onClick={() => navigate('/worker/timesheets')}>Complete</Button></TableCell>
                        </TableRow>
                    ))}
                    {!loading && incompleteTimesheets.length === 0 && <TableEmpty  colSpan={6}>No timesheets yet.</TableEmpty>}
                </tbody>
            </Table>
        </div>
    )

    return (
        <div>
            <Header />
            <LoadingPopup visible={loading} message='Loading...' />
            <div className='pageShell'>
                <Sidebar />
                <PageContent as="div" className='pageContent pageContentPadded workerPageContent workerPortalPage workerOverviewPage'>
                    <PageHeader title={<>Overview</>} subtitle={<>Your allocations and weekly activity</>} />
                    <div className='workerOverviewLayout'>
                        <div className='workerOverviewAllocationStack'>
                            <div className='workerOverviewKpiRow'>
                                <StatCard label={<>Allocations this week</>} value={<>{shiftsThisWeek}</>} tone="blue" icon={<><ClipboardListIcon /></>} />
                                <StatCard label={<>Hours this week</>} value={<>{hoursWorkedThisWeek}</>} tone="blue" icon={<><ClockIcon /></>} />
                                <StatCard label={<>Invoice this week</>} value={<>{loading ? '-' : weeklyInvoice === null ? '-' : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(weeklyInvoice)}</>} tone="blue" icon={<><ReceiptIcon /></>} />
                            </div>

                            <Section title='Pending Allocations' className='width-100 workerPendingAllocationCard' variant='card'>
                                <AllocationTable rows={pendingRowsForDisplay} pending />
                            </Section>
                            <Section title='Accepted Allocations' className='width-100' variant='card'>
                                <AllocationTable rows={upcomingRowsForDisplay} />
                            </Section>
                            <Section title='Incomplete Timesheets' className='width-100 workerPendingAllocationCard' variant='card'>
                                <IncompleteTimesheetTable />
                            </Section>
                            <div className='workerOverviewMobileSummaries'>
                                <button type='button' className={pendingRowsForDisplay.length ? 'workerOverviewMobileSummary workerOverviewMobileSummaryPending workerOverviewMobileSummaryAlert' : 'workerOverviewMobileSummary workerOverviewMobileSummaryPending'} onClick={() => navigate('/workers?view=allocations&allocationTab=pending')}>
                                    <span className='workerOverviewMobileSummaryIcon' data-icon-category='allocations'><ClockIcon /></span>
                                    <strong>Pending Allocations</strong>
                                    <b>{pendingRowsForDisplay.length}</b>
                                    <ChevronRightIcon />
                                </button>
                                <button type='button' className='workerOverviewMobileSummary workerOverviewMobileSummaryAccepted' onClick={() => navigate('/workers?view=allocations&allocationTab=accepted')}>
                                    <span className='workerOverviewMobileSummaryIcon' data-icon-category='allocations'><VerifiedIcon /></span>
                                    <strong>Accepted Allocations</strong>
                                    <b>{upcomingRowsForDisplay.length}</b>
                                    <ChevronRightIcon />
                                </button>
                                <button type='button' className={incompleteTimesheets.length ? 'workerOverviewMobileSummary workerOverviewMobileSummaryIncomplete workerOverviewMobileSummaryAlert' : 'workerOverviewMobileSummary workerOverviewMobileSummaryIncomplete'} onClick={() => navigate('/worker/timesheets')}>
                                    <span className='workerOverviewMobileSummaryIcon' data-icon-category='timesheets'><XIcon /></span>
                                    <strong>Incomplete Timesheets</strong>
                                    <b>{incompleteTimesheets.length}</b>
                                    <ChevronRightIcon />
                                </button>
                            </div>
                        </div>
                        <aside className='workerOverviewKpis'>
                            <Section title='Calendar' className='width-100 workerOverviewCalendarCard' variant='card'>
                                <div className='workerOverviewMobileCalendarToolbar'>
                                    <strong>{calendarViewMode === 'month' ? format(calendarActiveStartDate, 'MMMM yyyy') : `Week of ${format(calendarWeekStart, 'd MMM yyyy')}`}</strong>
                                    <Button title='Previous calendar period' variant="secondary" size="icon" type='button' aria-label='Previous calendar period' onClick={() => {
                                        const date = new Date(calendarDate)
                                        if (calendarViewMode === 'month') date.setMonth(date.getMonth() - 1, 1)
                                        else date.setDate(date.getDate() - 7)
                                        setCalendarDate(date)
                                        setCalendarActiveStartDate(new Date(date.getFullYear(), date.getMonth(), 1))
                                    }}><ChevronRightIcon style={{ transform: 'rotate(180deg)' }} /></Button>
                                    <Button title='Next calendar period' variant="secondary" size="icon" type='button' aria-label='Next calendar period' onClick={() => {
                                        const date = new Date(calendarDate)
                                        if (calendarViewMode === 'month') date.setMonth(date.getMonth() + 1, 1)
                                        else date.setDate(date.getDate() + 7)
                                        setCalendarDate(date)
                                        setCalendarActiveStartDate(new Date(date.getFullYear(), date.getMonth(), 1))
                                    }}><ChevronRightIcon /></Button>
                                    <Tabs className='workerOverviewMobileCalendarToggle'>
                                        <Tabs.Tab active={calendarViewMode === 'month'} type='button' className={calendarViewMode === 'month' ? 'active' : ''} onClick={() => setCalendarViewMode('month')}>Month</Tabs.Tab>
                                        <Tabs.Tab active={calendarViewMode === 'week'} type='button' className={calendarViewMode === 'week' ? 'active' : ''} onClick={() => setCalendarViewMode('week')}>Week</Tabs.Tab>
                                    </Tabs>
                                </div>
                                <div className='workerOverviewCalendarToolbar'>
                                    <div className='workerOverviewCalendarControls'>
                                        <FieldLabel>
                                            <span>Month</span>
                                            <UiSelect
                                                value={calendarActiveStartDate.getMonth()}
                                                onChange={event => {
                                                    const date = new Date(calendarActiveStartDate.getFullYear(), Number(event.target.value), 1)
                                                    setCalendarActiveStartDate(date)
                                                    setCalendarDate(date)
                                                }}
                                            >
                                                {calendarMonthOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                                            </UiSelect>
                                        </FieldLabel>
                                        <FieldLabel>
                                            <span>Year</span>
                                            <UiSelect
                                                value={calendarActiveStartDate.getFullYear()}
                                                onChange={event => {
                                                    const date = new Date(Number(event.target.value), calendarActiveStartDate.getMonth(), 1)
                                                    setCalendarActiveStartDate(date)
                                                    setCalendarDate(date)
                                                }}
                                            >
                                                {getCalendarYearOptions(calendarActiveStartDate.getFullYear()).map(year => <option key={year} value={year}>{year}</option>)}
                                            </UiSelect>
                                        </FieldLabel>
                                    </div>
                                    <Tabs className='allocationSegment workerOverviewCalendarViewToggle' aria-label='Calendar view'>
                                        <Tabs.Tab active={calendarViewMode === 'month'} type='button' className={calendarViewMode === 'month' ? 'allocationSegmentButton allocationSegmentButtonActive' : 'allocationSegmentButton'} onClick={() => setCalendarViewMode('month')}>Month</Tabs.Tab>
                                        <Tabs.Tab active={calendarViewMode === 'week'} type='button' className={calendarViewMode === 'week' ? 'allocationSegmentButton allocationSegmentButtonActive' : 'allocationSegmentButton'} onClick={() => setCalendarViewMode('week')}>Week</Tabs.Tab>
                                    </Tabs>
                                </div>
                                {calendarViewMode === 'month' ? <Calendar
                                    value={calendarDate}
                                    onChange={setCalendarDate}
                                    activeStartDate={calendarActiveStartDate}
                                    onActiveStartDateChange={({ activeStartDate }) => activeStartDate && setCalendarActiveStartDate(activeStartDate)}
                                    calendarType='iso8601'
                                    locale='en-AU'
                                    formatDay={(_, date) => format(date, 'd')}
                                    tileClassName={({ date, view }) => view === 'month' ? getCalendarMarkerClass(date) : null}
                                    tileContent={({ date, view }) => view === 'month' ? renderCalendarHoverTarget(date) : null}
                                    showNeighboringMonth={false}
                                    showNavigation={false}
                                /> : <div className='workerOverviewWeekCalendar'>
                                    <div className='workerOverviewWeekRange'>
                                        <div><span>Week Start:</span><strong>{format(calendarWeekStart, 'EEE, d MMM')}</strong></div>
                                        <div className='workerOverviewWeekIconNavigation'>
                                            <Button variant="secondary" size="icon" type='button' aria-label='Previous' title='Previous' onClick={() => { const date = new Date(calendarDate); date.setDate(date.getDate() - 7); setCalendarDate(date); setCalendarActiveStartDate(new Date(date.getFullYear(), date.getMonth(), 1)) }}><ChevronRightIcon style={{ transform: 'rotate(180deg)' }} /></Button>
                                            <Button variant="secondary" size="icon" type='button' aria-label='Next' title='Next' onClick={() => { const date = new Date(calendarDate); date.setDate(date.getDate() + 7); setCalendarDate(date); setCalendarActiveStartDate(new Date(date.getFullYear(), date.getMonth(), 1)) }}><ChevronRightIcon /></Button>
                                        </div>
                                    </div>
                                    <div className='workerOverviewWeekGrid'>
                                        {calendarWeekDays.map(date => <div key={`heading-${date.toISOString()}`} className='workerOverviewWeekHeading'>{format(date, 'EEE')}</div>)}
                                        {calendarWeekDays.map(date => <button key={date.toISOString()} type='button' className={getCalendarMarkerClass(date)} onMouseEnter={event => showCalendarQuickView(event, date)} onMouseLeave={() => setCalendarQuickView(null)} onClick={() => setCalendarDate(date)}><abbr>{format(date, 'd')}</abbr></button>)}
                                    </div>
                                </div>}
                                {calendarQuickView && <div
                                    className='workerOverviewCalendarQuickView'
                                    role='tooltip'
                                    style={{ left: calendarQuickView.left, top: calendarQuickView.top }}
                                >
                                    <div><strong>Client</strong><span>{calendarQuickView.allocation.clientName || calendarQuickView.allocation.client || '-'}</span></div>
                                    <div><strong>Site</strong><span>{calendarQuickView.allocation.siteName || calendarQuickView.allocation.site || '-'}</span></div>
                                    <div><strong>Time</strong><span>{calendarQuickView.allocation.date ? formatTime(new Date(calendarQuickView.allocation.date)) : '-'}</span></div>
                                </div>}
                            </Section>
                            <Section title='My Leave' className='width-100 workerOverviewLeaveCard' variant='card'>
                                <div className='workerOverviewLeaveList'>
                                    {leaveRowsForDisplay.map(leave => (
                                        <div key={leave.id} className='workerOverviewLeaveItem'>
                                            <CalendarIcon />
                                            <span>{leave.startDate ? format(new Date(leave.startDate), 'd MMM yyyy') : '-'} to {leave.endDate ? format(new Date(leave.endDate), 'd MMM yyyy') : '-'}</span>
                                        </div>
                                    ))}
                                    {!loading && leaveRowsForDisplay.length === 0 && <div className='workerOverviewLeaveEmpty'>No upcoming leave.</div>}
                                </div>
                            </Section>
                        </aside>
                    </div>
                </PageContent>
            </div>
        </div>
    )
}

export default WorkerOverview
